import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Wallet } from 'lucide-react';

// The tender types BIGTIME accepts in a physical or online sale.
// Each carries a rough "kind" so the UI can adapt (transfer methods force a
// reference number; cash tenders don't).
export const PAYMENT_METHODS = [
  { id: 'efectivo_usd', label: 'Efectivo $ USD',   currency: 'USD',  kind: 'cash' },
  { id: 'efectivo_eur', label: 'Efectivo € EUR',   currency: 'EUR',  kind: 'cash' },
  { id: 'cop',          label: 'Efectivo COP',      currency: 'COP',  kind: 'cash' },
  { id: 'bs',           label: 'Efectivo Bs (VES)', currency: 'VES',  kind: 'cash' },
  { id: 'usdt',         label: 'USDT (Binance)',    currency: 'USDT', kind: 'transfer' },
  { id: 'zelle',        label: 'Zelle',             currency: 'USD',  kind: 'transfer' },
  { id: 'pagomovil',    label: 'Pago Móvil',        currency: 'VES',  kind: 'transfer' },
  { id: 'binance',      label: 'Binance Pay',       currency: 'USDT', kind: 'transfer' },
];

const needsReference = (methodId) => {
  const m = PAYMENT_METHODS.find((x) => x.id === methodId);
  return m && m.kind === 'transfer';
};

/**
 * PaymentBreakdown
 * Lets the seller / customer register multiple tenders per order. The parent
 * gets the current list via `onChange` and can validate before submit.
 */
export default function PaymentBreakdown({ payments = [], onChange, totalUsd = 0 }) {
  const [draft, setDraft] = useState({ method: 'efectivo_usd', amount: '', reference: '' });
  const [err, setErr] = useState('');

  const add = () => {
    const method = PAYMENT_METHODS.find((m) => m.id === draft.method);
    if (!method) return;
    const amt = parseFloat(draft.amount);
    if (isNaN(amt) || amt <= 0) { setErr('Monto inválido'); return; }
    if (needsReference(method.id) && !draft.reference.trim()) { setErr('La referencia es requerida para este método'); return; }
    onChange([...payments, {
      method: method.id, currency: method.currency,
      amount: amt, reference: draft.reference.trim(),
    }]);
    setDraft({ method: draft.method, amount: '', reference: '' });
    setErr('');
  };
  const remove = (idx) => onChange(payments.filter((_, i) => i !== idx));

  // USD-equivalent isn't computed here (rates vary by tender). We show the
  // running total in each currency so the seller can eyeball reconciliation.
  const byCurrency = payments.reduce((acc, p) => ({ ...acc, [p.currency]: (acc[p.currency] || 0) + p.amount }), {});

  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50" data-testid="payment-breakdown">
      <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5 mb-2">
        <Wallet size={14} /> Métodos de pago (opcional, uno o varios)
      </p>
      {payments.length > 0 && (
        <div className="space-y-1 mb-2">
          {payments.map((p, i) => {
            const m = PAYMENT_METHODS.find((x) => x.id === p.method);
            return (
              <div key={i} className="flex items-center gap-2 bg-white border rounded-lg px-2 py-1.5 text-xs" data-testid={`payment-line-${i}`}>
                <span className="font-bold flex-1 truncate">{m ? m.label : p.method}</span>
                <span className="font-mono font-bold text-[#1B2A4A]">{p.amount.toFixed(2)} {p.currency}</span>
                {p.reference && <span className="text-slate-500 font-mono">ref: {p.reference}</span>}
                <button onClick={() => remove(i)} className="text-red-500 p-1" data-testid={`payment-remove-${i}`}><Trash2 size={12} /></button>
              </div>
            );
          })}
          <div className="text-[10px] text-slate-500 pt-1">
            Suma por moneda: {Object.entries(byCurrency).map(([c, a]) => `${a.toFixed(2)} ${c}`).join(' · ')}
            {totalUsd ? ` · Total pedido: $${totalUsd.toFixed(2)}` : ''}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <select
          value={draft.method}
          onChange={(e) => setDraft({ ...draft, method: e.target.value })}
          className="h-9 border border-slate-300 rounded-md px-2 text-sm bg-white col-span-2"
          data-testid="payment-method-select"
        >
          {PAYMENT_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <Input
          type="number" step="0.01" value={draft.amount}
          onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
          placeholder="Monto" className="h-9 text-sm"
          data-testid="payment-amount"
        />
        {needsReference(draft.method) ? (
          <Input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            placeholder="Nº referencia *" className="h-9 text-sm"
            data-testid="payment-reference"
          />
        ) : <div className="text-[11px] text-slate-500 self-center pl-1">Sin referencia (efectivo)</div>}
      </div>
      {err && <p className="text-[11px] text-red-600 mt-1" data-testid="payment-err">{err}</p>}
      <Button onClick={add} type="button" className="mt-2 h-8 rounded-full bg-[#1B2A4A] text-yellow-300 text-xs px-3" data-testid="payment-add">
        <Plus size={12} className="mr-1" /> Agregar pago
      </Button>
    </div>
  );
}
